/**
 * Admin mirror of shared `ORDER_FULFILLMENT_STATUSES` (P1 / Q3 Spec-override).
 * Installed `pi-kiosk-shared` may lag `./order` export — keep local const parity.
 */

export const ORDER_FULFILLMENT_STATUSES = [
  'PENDING_PAYMENT',
  'ACCEPTED',
  'PREPARING',
  'READY_FOR_PICKUP',
  'COLLECTED',
  'CANCELED',
  'READY_TO_SHIP',
  'SHIPPED',
  'DELIVERED',
] as const;

export type OrderFulfillmentStatus = (typeof ORDER_FULFILLMENT_STATUSES)[number];

/** Mirrors `StatusBadgeTone` without importing StatusBadge (avoid cycle). */
export type FulfillmentStatusBadgeTone = 'neutral' | 'success' | 'warn' | 'danger' | 'info';

export function isOrderFulfillmentStatus(value: unknown): value is OrderFulfillmentStatus {
  return (
    typeof value === 'string' &&
    (ORDER_FULFILLMENT_STATUSES as readonly string[]).includes(value)
  );
}

function assertNever(value: never): never {
  throw new Error(`Unhandled fulfillment status: ${String(value)}`);
}

/** Exhaustive StatusBadge tone map — must cover ACCEPTED (no default fall-through for known union). */
export function statusBadgeToneForFulfillmentStatus(
  status: OrderFulfillmentStatus,
): FulfillmentStatusBadgeTone {
  switch (status) {
    case 'PENDING_PAYMENT':
      return 'warn';
    case 'ACCEPTED':
      return 'info';
    case 'PREPARING':
      return 'info';
    case 'READY_FOR_PICKUP':
    case 'READY_TO_SHIP':
      return 'success';
    case 'SHIPPED':
      return 'info';
    case 'DELIVERED':
      return 'neutral';
    case 'CANCELED':
      return 'danger';
    case 'COLLECTED':
      return 'neutral';
    default:
      return assertNever(status);
  }
}

export function fulfillmentStatusAccentClassForStatus(status: OrderFulfillmentStatus): string {
  switch (status) {
    case 'PENDING_PAYMENT':
      return 'bg-[var(--color-an-warn)]';
    case 'ACCEPTED':
      return 'bg-[var(--color-an-info)]';
    case 'PREPARING':
      return 'bg-[var(--color-an-info)]';
    case 'READY_FOR_PICKUP':
    case 'READY_TO_SHIP':
      return 'bg-[var(--color-an-success)]';
    case 'SHIPPED':
      return 'bg-[var(--color-an-info)]';
    case 'DELIVERED':
      return 'bg-[var(--color-an-text-muted)]';
    case 'CANCELED':
      return 'bg-[var(--color-an-danger)]';
    case 'COLLECTED':
      return 'bg-[var(--color-an-text-muted)]';
    default:
      return assertNever(status);
  }
}
